import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import { apiPost } from '../../services/api';
import { countries } from '../../utils/countries';
import heroCourseImage from '../../assets/images/hydrabad/course-face-yoga-ttc.webp';
import centreHeadImage from '../../assets/images/hydrabad/centre-head-hyderabad.webp';
import '../../pcos-management-style.css';
import './HydrabadFaceYoga.css';

/* ─── Accordion Item Component (Syllabus & FAQs) ─────────────────────────── */
const AccordionItem = ({ num, title, subtitle, children, isActive, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');

  useEffect(() => {
    setHeight(isActive ? `${contentRef.current ? contentRef.current.scrollHeight : 500}px` : '0px');
  }, [isActive]);

  return (
    <div className={`fy-accordion-item ${isActive ? 'active' : ''}`}>
      <button
        className="fy-accordion-btn"
        onClick={onToggle}
        aria-expanded={isActive}
        type="button"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          {num && <span className="fy-accordion-num">{num}</span>}
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, textAlign: 'left' }}>
            <span style={{ fontWeight: 700 }}>{title}</span>
            {subtitle && <span className="fy-accordion-subtitle">{subtitle}</span>}
          </span>
        </span>
        <div className="fy-accordion-icon">
          <i className={`bi ${isActive ? 'bi-dash' : 'bi-plus'}`} aria-hidden="true"></i>
        </div>
      </button>
      <div className="fy-accordion-content" style={{ maxHeight: height }}>
        <div ref={contentRef} className="fy-accordion-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Hyderabad Face Yoga Component ─────────────────────────────────── */
const HydrabadFaceYoga = () => {
  const navigate = useNavigate();

  // Active accordion states
  const [activeModule, setActiveModule] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    countryCode: '+91',
    email: '',
    learningFormat: 'Classroom Teacher Training',
    preferredTiming: '',
    preferredLanguage: 'English',
    question: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  /* Scoped CSS class on body for design system inheritance */
  useLayoutEffect(() => {
    document.body.classList.add('pcos-page');
    return () => { document.body.classList.remove('pcos-page'); };
  }, []);

  /* Page-specific SEO & Structured Data */
  useEffect(() => {
    document.title = "Face Yoga Course in Hyderabad | PranaFlow Academy";

    // Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      'content',
      "Explore in-person face yoga classes and one-to-one teacher training in Hyderabad with PranaFlow Academy. Enquire about the 30-hour, 1-month course."
    );

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/Centre/hydrabad/face-yoga-teacher-training");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "Face Yoga Course in Hyderabad | PranaFlow Academy" },
      { property: 'og:description', content: "Explore in-person face yoga classes and one-to-one teacher training in Hyderabad with PranaFlow Academy. Enquire about the 30-hour, 1-month course." },
      { property: 'og:url', content: "https://pranaflowacademy.com/Centre/hydrabad/face-yoga-teacher-training" },
      { property: 'og:type', content: "website" },
      { property: 'og:image', content: "https://pranaflowacademy.com/assets/hydrabad/course-face-yoga-ttc.webp" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "Face Yoga Course in Hyderabad | PranaFlow Academy" },
      { name: 'twitter:description', content: "Explore in-person face yoga classes and one-to-one teacher training in Hyderabad with PranaFlow Academy. Enquire about the 30-hour, 1-month course." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/assets/hydrabad/course-face-yoga-ttc.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    window.scrollTo(0, 0);

    // Dynamic JSON-LD Schema
    const scriptId = 'jsonld-schema-hyd-faceyoga';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    const courseSchema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": "https://pranaflowacademy.com/Centre/hydrabad/face-yoga-teacher-training#course",
      "name": "Face Yoga Course in Hyderabad",
      "description": "Learn face yoga through personal guidance, practical demonstrations, and face-to-face interaction at PranaFlow Academy’s Hyderabad centre. 30-hour, 1-month teacher training and one-to-one courses.",
      "url": "https://pranaflowacademy.com/Centre/hydrabad/face-yoga-teacher-training",
      "provider": {
        "@type": "EducationalOrganization",
        "name": "PranaFlow Academy",
        "url": "https://pranaflowacademy.com/"
      },
      "courseMode": "in-person",
      "educationalCredentialAwarded": "PranaFlow Academy Face Yoga Teacher Training Certificate",
      "inLanguage": "en"
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pranaflowacademy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Hyderabad Centre",
          "item": "https://pranaflowacademy.com/Centre/hydrabad"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Face Yoga Course in Hyderabad",
          "item": "https://pranaflowacademy.com/Centre/hydrabad/face-yoga-teacher-training"
        }
      ]
    };

    script.textContent = JSON.stringify([courseSchema, breadcrumbSchema]);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  // Smooth scroll handler
  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const headerOffset = 90;
    const elementPosition = el.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  };

  // Button handler for specific learning format selection
  const handleSelectFormatAndScroll = (format) => {
    if (format) {
      setFormData(prev => ({ ...prev, learningFormat: format }));
    }
    scrollToSection('enquiry-section');
  };

  // Button handler for workshop/special enquiry contexts
  const handleSpecialEnquiryAndScroll = (enquiryContext) => {
    setFormData(prev => {
      const currentQ = prev.question.trim();
      let updatedQ = currentQ;
      if (!currentQ.includes(enquiryContext)) {
        updatedQ = currentQ ? `${enquiryContext}\n${currentQ}` : enquiryContext;
      }
      return {
        ...prev,
        question: updatedQ
      };
    });
    scrollToSection('enquiry-section');
  };

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!formData.name.trim()) {
      setErrorMessage('Please enter your full name.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email.trim())) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    if (!formData.phone.trim()) {
      setErrorMessage('Please enter your mobile / WhatsApp number.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        centre_code: 'HYD-001',
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile: formData.phone.trim(),
        mobile_number: formData.phone.trim(),
        country_code: formData.countryCode,
        learning_mode: formData.learningFormat,
        preferred_timing: formData.preferredTiming ? formData.preferredTiming.trim() : '',
        preferred_language: formData.preferredLanguage ? formData.preferredLanguage.trim() : 'English',
        question: formData.question ? formData.question.trim() : '',
        source_page: '/Centre/hydrabad/face-yoga-teacher-training'
      };

      await apiPost('/save-centre-course-enquiry.php', payload);
      setIsSuccess(true);

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'pranaflow_centre_enquiry_success',
          centre_code: 'HYD-001',
          lead_source: 'Hyderabad Face Yoga Course Page',
          learning_mode: formData.learningFormat,
          course: 'Face Yoga Teacher Training Course'
        });
      }
    } catch (err) {
      console.error('Hyderabad Face Yoga Enquiry Error:', err);
      setErrorMessage(err.message || 'Failed to submit enquiry. Please try again or reach out directly on WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* 10 Modules Syllabus Data */
  const modules = [
    {
      num: '01',
      title: 'Introduction to Face Yoga',
      desc: 'Explore the principles of face yoga, its place in a self-care routine, and personal and professional learning goals.'
    },
    {
      num: '02',
      title: 'Facial Anatomy & Structure',
      desc: 'Study facial muscle groups and the relationship between muscles, skin, and underlying structures.'
    },
    {
      num: '03',
      title: 'Understanding Facial Ageing',
      desc: 'Discuss changes in facial appearance, relevant lifestyle factors, and the limits of cosmetic claims.'
    },
    {
      num: '04',
      title: '85+ Face Yoga Movements',
      desc: 'Learn zone-by-zone exercises through demonstration, supervised practice, and technique feedback.'
    },
    {
      num: '05',
      title: 'Jawline, Cheek & Neck Practice',
      desc: 'Explore lower-face coordination, jawline exercises, neck posture, and careful movement.'
    },
    {
      num: '06',
      title: 'Facial Massage, Acupressure & Lymphatic Drainage',
      desc: 'Study gentle techniques, pressure awareness, hygiene, and situations in which a practice should be avoided or referred for professional advice.'
    },
    {
      num: '07',
      title: 'Breathwork, Mudras & Relaxation',
      desc: 'Learn how these practices can be incorporated into a structured face yoga session.'
    },
    {
      num: '08',
      title: 'Skin Health, Nutrition & Ayurvedic Self-Care',
      desc: 'Explore general nutrition and self-care concepts while understanding the boundaries between wellness education and medical treatment.'
    },
    {
      num: '09',
      title: 'Personal Practice & Routine Planning',
      desc: 'Build manageable routines and learn to discuss appearance-related goals without promising specific results.'
    },
    {
      num: '10',
      title: 'Teaching Methodology, Assessment & Certification',
      desc: 'Practise explaining, demonstrating, and sequencing a class. Complete the required teaching assessment and receive feedback.'
    }
  ];

  /* 14 Points Covered */
  const coveragePoints = [
    "Face yoga foundations and approaches to self-care.",
    "Facial muscles, skin, and underlying structures.",
    "Facial ageing and realistic expectations.",
    "Forehead, brow, and eye-area movement awareness.",
    "Cheek and lip-area exercises.",
    "Jawline face yoga and lower-face coordination.",
    "Neck posture and movement.",
    "Gentle facial massage and lymphatic drainage techniques.",
    "Facial acupressure and relaxation practices.",
    "Breathwork and mudras.",
    "Skin-care principles and nutrition fundamentals.",
    "Personal routine planning.",
    "Class sequencing and workshop planning.",
    "Practical teaching assessment and feedback."
  ];

  /* 5 Myths & Facts */
  const myths = [
    {
      m: '“I will see a complete transformation in a few days.”',
      f: 'We do not promise a fixed cosmetic result or timeline. The course focuses on learning and developing a practice.'
    },
    {
      m: '“Face yoga is the same as a surgical facelift.”',
      f: 'Our programme does not offer surgery or promise surgical results.'
    },
    {
      m: '“One workshop gives me a teacher training certificate.”',
      f: 'The teacher training certificate requires completion of the full programme and its assessments.'
    },
    {
      m: '“I need a yoga background to start.”',
      f: 'Beginners can enquire about the course. Our team will explain the expectations and learning format.'
    },
    {
      m: '“The certificate is a medical qualification.”',
      f: 'It is a PranaFlow Academy training certificate. It does not qualify you to diagnose or treat medical conditions.'
    }
  ];

  /* 7 Why Choose PranaFlow Cards */
  const whyPoints = [
    {
      title: "Learn Face to Face",
      desc: "Watch demonstrations closely and ask questions during practice."
    },
    {
      title: "Choose Individual Attention",
      desc: "Enquire about one-to-one training when you prefer a personal learning format."
    },
    {
      title: "Connected to Our Rishikesh Main Branch",
      desc: "Study at the Hyderabad centre of an academy rooted in Rishikesh."
    },
    {
      title: "Follow a Structured Curriculum",
      desc: "Move through anatomy, exercises, massage techniques, breathwork, and teaching methodology."
    },
    {
      title: "Receive Practical Feedback",
      desc: "Develop your ability to demonstrate and explain techniques through supervised practice."
    },
    {
      title: "Understand Your Certification",
      desc: "Know the attendance, assignment, and assessment requirements before enrolling."
    },
    {
      title: "Contact a Dedicated Centre Team",
      desc: "Get help with course selection, schedules, and learning arrangements."
    }
  ];

  /* 11 Included Items */
  const includedItems = [
    "30 hours of structured training across one month.",
    "A 10-module curriculum.",
    "Instruction in 85+ face yoga exercises and micro-movements.",
    "Facial anatomy and movement awareness.",
    "Facial massage, acupressure, and lymphatic drainage techniques.",
    "Breathwork, mudras, and self-care concepts.",
    "General nutrition and skin-care education.",
    "Guided practice and opportunities to ask questions.",
    "Teaching methodology and class sequencing.",
    "Practical assessment and feedback.",
    "PranaFlow Academy certification after successful completion."
  ];

  /* 11 Hyderabad Face Yoga FAQs */
  const faqs = [
    {
      q: "Do I need previous experience?",
      a: "No previous yoga or beauty experience is required to enquire about this beginner-accessible programme. We will explain the course expectations before you join."
    },
    {
      q: "Do you offer one-to-one face yoga classes?",
      a: "Yes. Enquire about individual sessions for personal practice or one-to-one teacher training. The duration, schedule, fees, and certification requirements depend on the option selected."
    },
    {
      q: "How long is the teacher training course?",
      a: "The teacher training programme covers 30 hours over one month. Confirm the exact schedule with the Hyderabad team."
    },
    {
      q: "Who will be my face yoga trainer?",
      a: "Our team will confirm the teacher assigned to your classroom batch or individual sessions before enrollment. The Hyderabad centre is led by Dasari Nanditha, a Dietitian & Yoga Teacher."
    },
    {
      q: "Can I learn face yoga in Telugu — ఫేస్ యోగా?",
      a: "Please mention Telugu as your preferred teaching language when enquiring. Our team will confirm whether Telugu instruction is available for your selected teacher and schedule."
    },
    {
      q: "What happens if I miss a classroom session?",
      a: "Contact the centre team to discuss the arrangements for your programme. Do not assume that recordings or replacement sessions are automatically included."
    },
    {
      q: "How much does the course cost?",
      a: "Contact us for the current classroom or one-to-one fee, inclusions, and payment options."
    },
    {
      q: "Will I receive a certificate?",
      a: "Students who successfully complete the full teacher training programme and its requirements receive the PranaFlow Academy Face Yoga Teacher Training Certificate."
    },
    {
      q: "Can I join only for personal practice?",
      a: "Yes. Ask about one-to-one personal practice sessions. These are separate from completing the full teacher training certification."
    },
    {
      q: "Do you offer bridal or corporate workshops?",
      a: "We welcome enquiries. Share your requirements so the team can confirm whether a suitable session can be arranged."
    },
    {
      q: "Can I take the course online instead?",
      a: (
        <div>
          <p style={{ margin: '0 0 12px 0' }}>Yes. Click below to view our dedicated online course page and its current details.</p>
          <Link
            to="/courses/face-yoga-teacher-training-course-online"
            className="fy-btn-primary"
            style={{ padding: '8px 18px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            Explore Online Face Yoga Teacher Training <i className="bi bi-arrow-right" aria-hidden="true"></i>
          </Link>
        </div>
      )
    }
  ];

  return (
    <div className="pcos-page public-site">
      <PublicHeader />

      <main className="main">
        <div className="fy-header-spacer"></div>

        {/* =====================================================================
            SECTION 1 — HERO
            ===================================================================== */}
        <section id="hero" className="fy-section">
          <div className="fy-hero-container">
            <div className="fy-hero-wrapper">
              <div className="row align-items-center gy-4">
                <div className="col-lg-6">
                  <span className="fy-hero-pill">
                    <i className="bi bi-geo-alt-fill text-danger me-1"></i> In-Person Teacher Training &amp; Classes · Hyderabad
                  </span>

                  <h1 className="fy-hero-title">
                    Face Yoga Course in Hyderabad
                  </h1>

                  <div className="fy-hero-subhead">
                    Become a confident face yoga practitioner and certified instructor in 1 month, in person, with PranaFlow's certified teachers
                  </div>

                  <p className="fy-hero-para">
                    Learn face yoga through personal guidance, practical demonstrations, and face-to-face interaction at PranaFlow Academy’s Hyderabad centre. Our 30-hour, 1-month teacher training course covers facial anatomy, 85+ face yoga exercises, facial massage, lymphatic drainage techniques, acupressure, breathwork, and teaching methodology.
                  </p>
                  <p className="fy-hero-para">
                    Choose classroom learning or enquire about one-to-one training with a face yoga trainer. Whether you are a beginner, yoga teacher, beauty professional, or someone exploring a more consistent self-care practice, we will help you understand the right learning option.
                  </p>

                  <div className="fy-btn-group">
                    <button
                      type="button"
                      className="fy-btn-primary"
                      onClick={() => handleSelectFormatAndScroll('Classroom Teacher Training')}
                    >
                      Enquire About Hyderabad Training <i className="bi bi-arrow-right-circle-fill" aria-hidden="true"></i>
                    </button>

                    <button
                      type="button"
                      className="fy-btn-secondary"
                      onClick={() => handleSelectFormatAndScroll('One-to-One Teacher Training')}
                    >
                      Ask About One-to-One Classes
                    </button>
                  </div>

                  {/* Online alternative banner */}
                  <div className="fy-hero-online-alt">
                    <span className="fy-hero-online-alt-text">
                      <i className="bi bi-laptop text-success" aria-hidden="true"></i>
                      <span>Prefer learning from home? Join our live online course.</span>
                    </span>
                    <Link
                      to="/courses/face-yoga-teacher-training-course-online"
                      className="fy-hero-online-alt-btn"
                    >
                      Explore Online Face Yoga Teacher Training <i className="bi bi-arrow-right" aria-hidden="true"></i>
                    </Link>
                  </div>

                  <div className="fy-trust-badges">
                    <span><i className="bi bi-clock-fill" aria-hidden="true"></i> 30 Hours / 1 Month</span>
                    <span><i className="bi bi-geo-alt-fill" aria-hidden="true"></i> In-Person Learning · Hyderabad</span>
                    <span><i className="bi bi-person-fill-check" aria-hidden="true"></i> One-to-One Training Available</span>
                    <span><i className="bi bi-journal-bookmark-fill" aria-hidden="true"></i> 10 Modules · 85+ Exercises</span>
                    <span><i className="bi bi-check-circle-fill" aria-hidden="true"></i> Practical Teaching Assessment</span>
                    <span><i className="bi bi-award-fill" aria-hidden="true"></i> PranaFlow Academy Certificate</span>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-hero-img-container">
                    <img
                      src={heroCourseImage}
                      alt="Face Yoga Teacher Training Course in Hyderabad"
                      loading="eager"
                      width="600"
                      height="600"
                    />
                  </div>
                </div>
              </div>

              {/* Strip Callout */}
              <div className="fy-strip-container">
                <div className="fy-strip-grid">
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-clock-history" aria-hidden="true"></i></div>
                    <div className="fy-strip-label">30 Hours<br />1 Month</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-geo-alt-fill" aria-hidden="true"></i></div>
                    <div className="fy-strip-label">In-Person<br />Hyderabad</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-person-video" aria-hidden="true"></i></div>
                    <div className="fy-strip-label">One-to-One<br />Options</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-person-lines-fill" aria-hidden="true"></i></div>
                    <div className="fy-strip-label">85+ Face<br />Movements</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-journal-check" aria-hidden="true"></i></div>
                    <div className="fy-strip-label">Teaching<br />Assessment</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-award-fill" aria-hidden="true"></i></div>
                    <div className="fy-strip-label">Academy<br />Certificate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 2 — COURSE INTRODUCTION
            ===================================================================== */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-plan-box">
              <h2 className="fy-serif-heading mb-4 text-center text-lg-start">
                Face Yoga Classes in Hyderabad with Personal Guidance
              </h2>

              <div className="row gy-4 align-items-center">
                <div className="col-lg-6">
                  <p className="fy-hero-para">
                    You do not need a yoga or beauty background to begin. Some students come to develop a personal practice; others want to learn how to guide students of their own.
                  </p>
                  <p className="fy-hero-para">
                    Our face yoga classes in Hyderabad combine explanation, demonstration, and supervised practice. You can ask questions as you learn, receive feedback on your technique, and understand how to build a routine with care.
                  </p>
                  <p className="fy-hero-para">
                    PranaFlow Academy’s main branch is in Rishikesh. Our Hyderabad centre brings the academy’s commitment to structured education and carefully selected teachers closer to students who prefer classroom learning.
                  </p>
                  <div className="mt-4">
                    <button
                      type="button"
                      className="fy-btn-primary"
                      onClick={() => scrollToSection('syllabus')}
                    >
                      Explore the Hyderabad Course Syllabus <i className="bi bi-arrow-down-circle ms-1" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>

                <div className="col-lg-6">
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '14px' }}>
                    What you will study:
                  </h3>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>85+ face yoga exercises and micro-movements.</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Facial anatomy and muscle awareness.</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Facial massage and gentle lymphatic drainage techniques.</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Facial acupressure, breathwork, and mudras.</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Neck posture and lower-face movement awareness.</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Ayurvedic self-care concepts and nutrition fundamentals.</span></div>
                  <div className="fy-benefit-pill mb-0"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Class sequencing, teaching practice, and assessment.</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 3 — CHOOSE YOUR LEARNING FORMAT
            ===================================================================== */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <span className="fy-eyebrow">LEARN IN A WAY THAT SUITS YOU</span>
              <h2 className="fy-serif-heading">Classroom Learning or One-to-One Face Yoga in Hyderabad</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-format-grid">
              {/* Format 1: Classroom Training */}
              <div className="fy-format-card">
                <div>
                  <div className="fy-format-card-header">
                    <div className="fy-format-icon">
                      <i className="bi bi-people-fill" aria-hidden="true"></i>
                    </div>
                    <h3 className="fy-format-title">In-Person Classroom Training</h3>
                  </div>
                  <div className="fy-format-body">
                    <p>
                      Learn alongside fellow students through demonstrations, guided practice, discussions, and teaching exercises. Follow a structured course while receiving feedback from your teacher.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="fy-btn-primary w-100"
                  onClick={() => handleSelectFormatAndScroll('Classroom Teacher Training')}
                >
                  Ask About Classroom Batches <i className="bi bi-arrow-right" aria-hidden="true"></i>
                </button>
              </div>

              {/* Format 2: One-to-One Training */}
              <div className="fy-format-card">
                <div>
                  <div className="fy-format-card-header">
                    <div className="fy-format-icon blush">
                      <i className="bi bi-person-check-fill" aria-hidden="true"></i>
                    </div>
                    <h3 className="fy-format-title">One-to-One Face Yoga Training</h3>
                  </div>
                  <div className="fy-format-body">
                    <p>
                      Prefer individual attention? Work directly with a face yoga trainer, with more time to discuss your questions, review your technique, and practise at an agreed pace.
                    </p>
                    <p>
                      Tell us whether you want personal practice sessions or the complete teacher training programme. The team will explain the duration, fees, and requirements for your chosen option.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="fy-btn-secondary w-100"
                  onClick={() => handleSelectFormatAndScroll('One-to-One Teacher Training')}
                >
                  Enquire About One-to-One Training <i className="bi bi-arrow-right" aria-hidden="true"></i>
                </button>
              </div>
            </div>

            <div className="small text-muted fst-italic text-center mt-4" style={{ maxWidth: '820px', margin: '24px auto 0' }}>
              Personal practice sessions and the full certification course are different learning options. Certification requires completion of the full programme and its assessments.
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 4 — WHAT YOU WILL COVER
            ===================================================================== */}
        <section id="coverage" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Understand the Practice Before You Teach It</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-stretch">
              <div className="col-lg-8">
                <div className="fy-highlights-grid">
                  <div>
                    {coveragePoints.slice(0, 5).map((point, idx) => (
                      <div key={idx} className="fy-check-bullet">
                        <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>

                  <div>
                    {coveragePoints.slice(5, 10).map((point, idx) => (
                      <div key={idx} className="fy-check-bullet">
                        <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>

                  <div>
                    {coveragePoints.slice(10, 14).map((point, idx) => (
                      <div key={idx} className="fy-check-bullet">
                        <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="fy-bonus-card">
                  <div className="fy-bonus-icon"><i className="bi bi-journal-bookmark-fill" aria-hidden="true"></i></div>
                  <div className="fy-bonus-title">LEARNING RESOURCES</div>
                  <div className="fy-bonus-sub">Guides to Support Your Practice</div>
                  <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', lineHeight: 1.6, marginTop: '12px' }}>
                    The programme’s learning materials cover face mapping, exercise instructions, massage sequences, acupressure points, and class planning. Ask our team which resources are provided digitally or in print for your Hyderabad training.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 5 — COMPLETE SYLLABUS
            ===================================================================== */}
        <section id="syllabus" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap mb-4 text-center">
              <h2 className="fy-serif-heading">Your 10-Module Face Yoga Teacher Training Syllabus</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para" style={{ maxWidth: '750px', margin: '12px auto 0' }}>
                Follow a structured learning path across the 30-hour programme, moving from foundational understanding to guided practice and teaching.
              </p>
            </div>

            <div className="fy-syllabus-container">
              {modules.map((m, idx) => (
                <AccordionItem
                  key={idx}
                  num={m.num}
                  title={`Module ${m.num} — ${m.title}`}
                  isActive={activeModule === idx}
                  onToggle={() => setActiveModule(activeModule === idx ? null : idx)}
                >
                  <p style={{ fontSize: '0.95rem', color: 'var(--fy-text-main)', margin: 0, lineHeight: 1.7 }}>
                    {m.desc}
                  </p>
                </AccordionItem>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 6 — WHO THIS COURSE IS FOR
            ===================================================================== */}
        <section id="who-is-this-for" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center mb-5">
              <h2 className="fy-serif-heading">Find Your Reason to Learn Face Yoga</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-stretch">
              {/* Card 1: Self-Care */}
              <div className="col-lg-4 col-md-6 col-12">
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--fy-sage-border, #CBE0D2)',
                  borderRadius: '16px',
                  padding: '30px 24px',
                  boxShadow: 'var(--fy-shadow-card)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}>
                  <div>
                    <div style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '50%',
                      background: 'var(--fy-sage-light)',
                      color: 'var(--fy-sage-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      marginBottom: '16px'
                    }}>
                      <i className="bi bi-sparkles" aria-hidden="true"></i>
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '10px' }}>
                      For Your Own Self-Care
                    </h3>
                    <p style={{ fontStyle: 'italic', color: 'var(--fy-text-muted)', marginBottom: '14px', fontSize: '0.95rem' }}>
                      “I want a routine I understand and can practise consistently.”
                    </p>
                    <p style={{ color: 'var(--fy-text-main)', marginBottom: '20px', fontSize: '0.92rem', lineHeight: '1.65' }}>
                      If you have tried videos but remain unsure about technique, in-person learning gives you space to ask questions and practise with guidance.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="fy-btn-primary"
                    onClick={() => handleSelectFormatAndScroll('Personal Practice Classes')}
                    style={{ padding: '12px 20px', fontSize: '0.9rem', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    Explore Personal Practice Classes <i className="bi bi-arrow-right" aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              {/* Card 2: Age-Positive */}
              <div className="col-lg-4 col-md-6 col-12">
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--fy-sage-border, #CBE0D2)',
                  borderRadius: '16px',
                  padding: '30px 24px',
                  boxShadow: 'var(--fy-shadow-card)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}>
                  <div>
                    <div style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '50%',
                      background: 'var(--fy-blush-light)',
                      color: 'var(--fy-blush-dark)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      marginBottom: '16px'
                    }}>
                      <i className="bi bi-heart-fill" aria-hidden="true"></i>
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '10px' }}>
                      For an Age-Positive Approach
                    </h3>
                    <p style={{ fontStyle: 'italic', color: 'var(--fy-text-muted)', marginBottom: '14px', fontSize: '0.95rem' }}>
                      “I want to care for my face without unrealistic promises.”
                    </p>
                    <p style={{ color: 'var(--fy-text-main)', marginBottom: '20px', fontSize: '0.92rem', lineHeight: '1.65' }}>
                      Learn about facial movement, gentle massage, and self-care while developing realistic expectations about changes in appearance.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="fy-btn-primary"
                    onClick={() => handleSelectFormatAndScroll('')}
                    style={{ padding: '12px 20px', fontSize: '0.9rem', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    Ask About the Course <i className="bi bi-arrow-right" aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              {/* Card 3: Teachers & Professionals */}
              <div className="col-lg-4 col-md-6 col-12">
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--fy-sage-border, #CBE0D2)',
                  borderRadius: '16px',
                  padding: '30px 24px',
                  boxShadow: 'var(--fy-shadow-card)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}>
                  <div>
                    <div style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '50%',
                      background: 'var(--fy-sage-light)',
                      color: 'var(--fy-sage-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      marginBottom: '16px'
                    }}>
                      <i className="bi bi-award-fill" aria-hidden="true"></i>
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '10px' }}>
                      For Yoga Teachers &amp; Wellness Professionals
                    </h3>
                    <p style={{ fontStyle: 'italic', color: 'var(--fy-text-muted)', marginBottom: '14px', fontSize: '0.95rem' }}>
                      “I want structured training before guiding others.”
                    </p>
                    <p style={{ color: 'var(--fy-text-main)', marginBottom: '20px', fontSize: '0.92rem', lineHeight: '1.65' }}>
                      Develop your understanding of anatomy, demonstrations, verbal instructions, class planning, and professional boundaries.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="fy-btn-primary"
                    onClick={() => handleSelectFormatAndScroll('Classroom Teacher Training')}
                    style={{ padding: '12px 20px', fontSize: '0.9rem', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    Explore Teacher Training <i className="bi bi-arrow-right" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="small text-muted fst-italic text-center mt-4" style={{ maxWidth: '820px', margin: '24px auto 0' }}>
              Individual experiences and cosmetic results vary. Course completion reflects learning and assessment, not a guaranteed change in appearance.
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 7 — MEET YOUR CENTRE HEAD
            ===================================================================== */}
        <section id="centre-head" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-ch-profile-card">
              <div className="fy-ch-grid">
                <div className="fy-ch-photo-wrap">
                  <img
                    src={centreHeadImage}
                    alt="Dasari Nanditha — Centre Head, PranaFlow Academy Hyderabad"
                    className="fy-ch-photo"
                    width="320"
                    height="350"
                    loading="lazy"
                  />
                  <div className="fy-ch-badge">
                    <span>Centre Head — Hyderabad</span>
                  </div>
                </div>

                <div>
                  <div className="fy-ch-role-label">Meet Your Centre Head</div>
                  <h2 className="fy-ch-name">A Dedicated Lead for Your Learning Experience in Hyderabad</h2>
                  <div className="fy-ch-role-label" style={{ marginTop: '8px', color: 'var(--fy-text-muted)' }}>
                    Centre Head — PranaFlow Academy, Hyderabad
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginTop: '4px' }}>
                    Dasari Nanditha
                  </div>
                  <div className="fy-ch-title">
                    Dietitian &amp; Yoga Teacher | 7 Years of Experience
                  </div>

                  <div className="fy-ch-bio">
                    <p>
                      Dasari Nanditha is a <strong>Dietitian &amp; Yoga Teacher with 7 years of experience</strong>, bringing together knowledge of nutrition, yoga, and practical wellness education. She has taught <strong>800+ students</strong>, including <strong>300+ in Face Yoga, 300+ in Diet and Nutrition, and 250+ through PCOS &amp; PCOD-focused wellness guidance</strong>.
                    </p>
                    <p>
                      She brings this experience to PranaFlow Academy’s Hyderabad centre, supporting students through clear explanations, guided practice, and a patient teaching approach. Working with the academy’s central team, she also coordinates teachers, batch schedules, and student support to maintain an organised and supportive learning environment.
                    </p>
                  </div>

                  <div className="fy-ch-trainer-box">
                    <h3 className="fy-ch-trainer-title">Know Your Face Yoga Trainer Before You Join</h3>
                    <p style={{ margin: '0 0 16px 0', fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65 }}>
                      Ask our team who will lead your selected batch or one-to-one sessions. We can explain the teacher’s relevant background, the learning format, and what to expect from your training.
                    </p>
                    <button
                      type="button"
                      className="fy-btn-primary"
                      onClick={() => scrollToSection('enquiry-section')}
                    >
                      Speak with Our Hyderabad Team <i className="bi bi-chat-dots-fill ms-1" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 8 — FEES & NEXT BATCH (PROGRAMME DETAILS + ENQUIRY FORM)
            ===================================================================== */}
        <section id="enquiry-section" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center mb-4">
              <h2 className="fy-serif-heading">Face Yoga Course Fees &amp; Upcoming Training in Hyderabad</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-start justify-content-center">
              {/* Left Column: Programme Details Table */}
              <div className="col-lg-6">
                <div className="fy-programme-table-wrap">
                  <div className="fy-programme-table-header">
                    <h3 className="fy-programme-table-title">Programme Details</h3>
                  </div>

                  <table className="fy-programme-table">
                    <tbody>
                      <tr>
                        <th>Duration</th>
                        <td>30 Hours / 1 Month</td>
                      </tr>
                      <tr>
                        <th>Location</th>
                        <td>PranaFlow Academy — Hyderabad Centre</td>
                      </tr>
                      <tr>
                        <th>Mode</th>
                        <td>In-person classroom training</td>
                      </tr>
                      <tr>
                        <th>Individual option</th>
                        <td>One-to-one training by arrangement</td>
                      </tr>
                      <tr>
                        <th>Schedule</th>
                        <td>Confirm with the Hyderabad team</td>
                      </tr>
                      <tr>
                        <th>Curriculum</th>
                        <td>10 Modules · 85+ Exercises</td>
                      </tr>
                      <tr>
                        <th>Assessment</th>
                        <td>Practical teaching assessment and course requirements</td>
                      </tr>
                      <tr>
                        <th>Certificate</th>
                        <td>PranaFlow Academy Face Yoga Teacher Training Certificate</td>
                      </tr>
                      <tr>
                        <th>Fees</th>
                        <td>Enquire for the selected training format</td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="fy-table-note-box">
                    <p style={{ margin: '0 0 14px 0' }}>
                      Classroom and one-to-one fees may differ. Contact us for the current fee, available schedule, payment options, and everything included in your selected programme.
                    </p>
                    <button
                      type="button"
                      className="fy-btn-secondary w-100"
                      onClick={() => {
                        const inputEl = document.getElementById('name');
                        if (inputEl) inputEl.focus();
                      }}
                    >
                      Get Hyderabad Fees &amp; Batch Details <i className="bi bi-arrow-right ms-1" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>

                {/* Supporting Link for Online Training */}
                <div style={{ marginTop: '20px', padding: '16px 20px', borderRadius: '16px', background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', fontSize: '0.92rem' }}>
                  <div style={{ fontWeight: 600, color: 'var(--fy-sage-dark)', marginBottom: '4px' }}>
                    Looking for online training?
                  </div>
                  <Link
                    to="/courses/face-yoga-teacher-training-course-online"
                    style={{ color: 'var(--fy-sage-primary)', fontWeight: 700, textDecoration: 'underline' }}
                  >
                    View the online course, fees, and upcoming batches →
                  </Link>
                </div>
              </div>

              {/* Right Column: Local Enquiry Form */}
              <div className="col-lg-6">
                <div className="fy-enquiry-card" id="enquiry-form">
                  <h3 className="fy-enquiry-title">Enquire About Face Yoga in Hyderabad</h3>

                  {isSuccess ? (
                    <div className="fy-form-alert fy-form-alert-success text-center">
                      <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '1.4rem' }}></i>
                      <h4 style={{ fontWeight: 700, fontSize: '1.1rem', margin: '8px 0 6px' }}>Enquiry Received!</h4>
                      <p style={{ margin: 0 }}>
                        Thank you for reaching out. Our Hyderabad team will contact you shortly with batch schedules, fees, and training details.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      {errorMessage && (
                        <div className="fy-form-alert fy-form-alert-error">
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          <span>{errorMessage}</span>
                        </div>
                      )}

                      {/* Full Name */}
                      <div className="fy-form-group">
                        <label htmlFor="name" className="fy-form-label">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          className="fy-form-input"
                          placeholder="Your full name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      {/* Mobile / WhatsApp Number */}
                      <div className="fy-form-group">
                        <label htmlFor="phone" className="fy-form-label">Mobile / WhatsApp Number *</label>
                        <div className="fy-phone-row">
                          <select
                            name="countryCode"
                            className="fy-form-select fy-phone-cc"
                            value={formData.countryCode}
                            onChange={handleInputChange}
                            aria-label="Country Code"
                          >
                            {countries.map((c, i) => (
                              <option key={`${c.code}-${i}`} value={c.dial}>
                                {c.code.toUpperCase()} {c.dial}
                              </option>
                            ))}
                          </select>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            className="fy-form-input"
                            placeholder="WhatsApp mobile number"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      {/* Email Address */}
                      <div className="fy-form-group">
                        <label htmlFor="email" className="fy-form-label">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className="fy-form-input"
                          placeholder="name@example.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      {/* Preferred Learning Mode */}
                      <div className="fy-form-group">
                        <label className="fy-form-label">Preferred Learning Mode *</label>
                        <div className="fy-radio-group">
                          {[
                            'Classroom Teacher Training',
                            'One-to-One Teacher Training',
                            'Personal Practice Classes',
                            'Live Online'
                          ].map((fmt, idx) => (
                            <label
                              key={idx}
                              className={`fy-radio-option ${formData.learningFormat === fmt ? 'selected' : ''}`}
                            >
                              <input
                                type="radio"
                                name="learningFormat"
                                value={fmt}
                                checked={formData.learningFormat === fmt}
                                onChange={handleInputChange}
                              />
                              <span>{fmt}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Preferred Days & Timings */}
                      <div className="fy-form-group">
                        <label htmlFor="preferredTiming" className="fy-form-label">Preferred days and timings</label>
                        <input
                          type="text"
                          id="preferredTiming"
                          name="preferredTiming"
                          className="fy-form-input"
                          placeholder="e.g. Weekday mornings, Weekend batch"
                          value={formData.preferredTiming}
                          onChange={handleInputChange}
                        />
                      </div>

                      {/* Preferred Teaching Language */}
                      <div className="fy-form-group">
                        <label htmlFor="preferredLanguage" className="fy-form-label">Preferred teaching language</label>
                        <select
                          id="preferredLanguage"
                          name="preferredLanguage"
                          className="fy-form-select"
                          value={formData.preferredLanguage}
                          onChange={handleInputChange}
                        >
                          <option value="English">English</option>
                          <option value="Telugu (subject to confirmation)">Telugu — తెలుగు (Subject to confirmation)</option>
                          <option value="Hindi">Hindi</option>
                        </select>
                      </div>

                      {/* Your Question */}
                      <div className="fy-form-group">
                        <label htmlFor="question" className="fy-form-label">Your question</label>
                        <textarea
                          id="question"
                          name="question"
                          className="fy-form-textarea"
                          placeholder="Ask about batch start dates, one-to-one availability, fees, or course details..."
                          value={formData.question}
                          onChange={handleInputChange}
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        className="fy-btn-primary w-100"
                        disabled={isSubmitting}
                        style={{ padding: '14px', fontSize: '1rem', marginTop: '8px' }}
                      >
                        {isSubmitting ? 'Submitting Details...' : 'Request Course Details'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 9 — COMMON CONCERNS
            ===================================================================== */}
        <section id="concerns" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <h2 className="fy-serif-heading">Bring Your Questions About Your Face and Your Practice</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '750px', margin: '12px auto 0' }}>
                People explore face yoga for different reasons. Our focus is to help you understand the practices, learn appropriate technique, and set realistic expectations.
              </p>
            </div>

            <div className="row gy-4 mt-2">
              <div className="col-lg-6">
                <div style={{ background: '#ffffff', border: '1px solid var(--fy-sage-border)', borderRadius: '16px', padding: '26px 24px', height: '100%', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '12px' }}>
                    Jawline &amp; Double Chin Concerns
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65, margin: 0 }}>
                    Searching for <strong>face yoga for double chin in Hyderabad</strong>? Learn lower-face movements, jawline face yoga, and posture awareness with teacher guidance. We do not promise targeted fat loss or a particular jawline shape.
                  </p>
                </div>
              </div>

              <div className="col-lg-6">
                <div style={{ background: '#ffffff', border: '1px solid var(--fy-sage-border)', borderRadius: '16px', padding: '26px 24px', height: '100%', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '12px' }}>
                    Wrinkles &amp; Ageing
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65, margin: 0 }}>
                    If you are exploring <strong>face yoga for wrinkles or anti-ageing</strong>, we help you approach the subject through education and guided practice. We do not promise wrinkle removal or reversal of ageing.
                  </p>
                </div>
              </div>

              <div className="col-lg-6">
                <div style={{ background: '#ffffff', border: '1px solid var(--fy-sage-border)', borderRadius: '16px', padding: '26px 24px', height: '100%', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '12px' }}>
                    Questions About Face Fat Exercises
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65, margin: 0 }}>
                    Understand the difference between exercising facial muscles and claims about reducing facial fat. Your teacher will explain the purpose of each practice without promising facial slimming.
                  </p>
                </div>
              </div>

              <div className="col-lg-6">
                <div style={{ background: '#ffffff', border: '1px solid var(--fy-sage-border)', borderRadius: '16px', padding: '26px 24px', height: '100%', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '12px' }}>
                    A Natural Face Lift Without Surgery?
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65, margin: 0 }}>
                    If you are searching for a <strong>natural face lift without surgery in Hyderabad</strong>, it is important to understand that face yoga is a wellness practice, not a surgical facelift or a promise of equivalent results.
                  </p>
                </div>
              </div>

              <div className="col-lg-6">
                <div style={{ background: '#ffffff', border: '1px solid var(--fy-sage-border)', borderRadius: '16px', padding: '26px 24px', height: '100%', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '12px' }}>
                    Uncertainty About Technique
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65, margin: 0 }}>
                    Learn how to follow demonstrations, recognise discomfort, and ask for adjustments instead of relying on guesswork.
                  </p>
                </div>
              </div>

              <div className="col-lg-6">
                <div style={{ background: '#ffffff', border: '1px solid var(--fy-sage-border)', borderRadius: '16px', padding: '26px 24px', height: '100%', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '12px' }}>
                    Difficulty Staying Consistent
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65, margin: 0 }}>
                    Build a practical routine with a clearer understanding of what you are practising and why.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-4">
              <button
                type="button"
                className="fy-btn-primary"
                onClick={() => scrollToSection('enquiry-section')}
              >
                Discuss Your Learning Goals <i className="bi bi-chat-dots-fill ms-1" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 10 — LEARNING SUPPORT
            ===================================================================== */}
        <section className="fy-section fy-dashboard-section">
          <div className="fy-container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-7">
                <span className="fy-dashboard-badge">Support Beyond the Classroom</span>
                <h2 className="fy-serif-heading mb-3" style={{ color: '#ffffff', fontSize: '2rem' }}>
                  Keep Your Learning Organised Between Sessions
                </h2>
                <p className="fy-hero-para" style={{ color: '#C3DFC8' }}>
                  Classroom practice is one part of your learning. Use the course resources provided for your programme to review concepts, prepare questions, and work on assignments.
                </p>
                <p className="fy-hero-para" style={{ color: '#C3DFC8' }}>
                  Ask our team about the student dashboard, digital materials, and revision support included with your Hyderabad enrollment.
                </p>

                <div className="mt-4" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 'var(--fy-radius-md)', padding: '20px 24px' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#fbbf24', margin: '0 0 10px' }}>
                    Note on Classroom Arrangements:
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#C3DFC8', lineHeight: 1.6 }}>
                    Classroom recordings and missed-session arrangements must be confirmed for your selected batch. The recording provisions of the online course do not automatically apply to in-person training.
                  </p>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="fy-dashboard-card">
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>
                    Learning Support May Include:
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem', color: 'var(--fy-text-main)' }}>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Course schedules and announcements.</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Exercise guides and anatomy resources.</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Practice assignments.</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Assessment instructions.</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill" aria-hidden="true"></i><span>Teacher feedback and revision materials.</span></li>
                  </ul>
                  <button
                    type="button"
                    className="fy-btn-primary w-100"
                    onClick={() => scrollToSection('enquiry-section')}
                  >
                    Ask What Is Included <i className="bi bi-arrow-right ms-1" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 11 — MYTHS & EXPECTATIONS
            ===================================================================== */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <h2 className="fy-serif-heading">Face Yoga: Clear Expectations Before You Begin</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-3 mt-2">
              {myths.map((item, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div className="fy-myth-card">
                    <span className="fy-myth-badge">EXPECTATION vs REALITY</span>
                    <h3 className="fy-myth-q">{item.m}</h3>
                    <p className="fy-myth-a"><strong>Fact:</strong> {item.f}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 12 — WHY CHOOSE PRANAFLOW
            ===================================================================== */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <h2 className="fy-serif-heading">Why Choose PranaFlow for Face Yoga Teacher Training in Hyderabad?</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-diff-grid">
              {whyPoints.map((item, idx) => (
                <div key={idx} className="fy-diff-card">
                  <h3 className="fy-diff-title">{item.title}</h3>
                  <p className="fy-diff-desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 13 — YOUR ONE-MONTH JOURNEY
            ===================================================================== */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <h2 className="fy-serif-heading">From Understanding the Basics to Practising How to Teach</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-roadmap-grid">
              {/* Week 1 */}
              <div className="fy-roadmap-card month1">
                <div>
                  <span className="fy-roadmap-badge month1">Week 1</span>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>
                    Week 1 — Foundations &amp; Anatomy
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-muted)', lineHeight: 1.65, marginBottom: '14px' }}>
                    Explore face yoga principles, facial structures, and practice expectations. Begin learning with teacher demonstrations.
                  </p>
                </div>
                <div style={{ background: 'var(--fy-sage-light)', border: '1px solid var(--fy-sage-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.84rem', color: 'var(--fy-sage-dark)', fontWeight: 600 }}>
                  Focus: Theory, muscle awareness &amp; fundamentals.
                </div>
              </div>

              {/* Weeks 2–3 */}
              <div className="fy-roadmap-card month2">
                <div>
                  <span className="fy-roadmap-badge month2">Weeks 2–3</span>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>
                    Weeks 2–3 — Exercises &amp; Guided Practice
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-muted)', lineHeight: 1.65, marginBottom: '14px' }}>
                    Study facial movements, massage techniques, acupressure, breathwork, and routine building. Ask questions and work on your technique.
                  </p>
                </div>
                <div style={{ background: 'var(--fy-blush-light)', border: '1px solid var(--fy-blush-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.84rem', color: 'var(--fy-blush-dark)', fontWeight: 600 }}>
                  Focus: Demonstration, supervised practice &amp; routine building.
                </div>
              </div>

              {/* Week 4 */}
              <div className="fy-roadmap-card month3">
                <div>
                  <span className="fy-roadmap-badge month3">Week 4</span>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>
                    Week 4 — Teaching Practice &amp; Assessment
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-muted)', lineHeight: 1.65, marginBottom: '14px' }}>
                    Bring your learning together through class planning, teaching practice, assignments, and assessment.
                  </p>
                </div>
                <div style={{ background: '#DCF5E6', border: '1px solid #A7D3B6', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.84rem', color: '#1A5C33', fontWeight: 600 }}>
                  Focus: Teaching assessment &amp; certification.
                </div>
              </div>
            </div>

            <div className="small text-muted fst-italic text-center mt-4" style={{ maxWidth: '820px', margin: '24px auto 16px' }}>
              The exact timetable is shared for your selected classroom or one-to-one programme.
            </div>

            <div className="text-center">
              <button
                type="button"
                className="fy-btn-primary"
                onClick={() => scrollToSection('enquiry-section')}
              >
                Ask About the Next Training Schedule <i className="bi bi-calendar-event ms-1" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 14 — WHAT’S INCLUDED
            ===================================================================== */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <h2 className="fy-serif-heading">What You Will Learn in the Hyderabad Teacher Training Programme</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-receive-grid">
              {includedItems.map((item, idx) => (
                <div key={idx} className="fy-receive-card">
                  <div className="fy-receive-icon">
                    <i className="bi bi-check-lg" aria-hidden="true"></i>
                  </div>
                  <span className="fy-receive-text">{item}</span>
                </div>
              ))}
            </div>

            <div className="fy-receive-assurance">
              <i className="bi bi-info-circle-fill" aria-hidden="true"></i>
              <span>Confirm learning materials, dashboard access, payment arrangements, and any additional inclusions before enrolling.</span>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 15 — CERTIFICATION
            ===================================================================== */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-7">
                <span className="fy-eyebrow">ACADEMY CERTIFICATION</span>
                <h2 className="fy-serif-heading mb-3">
                  Face Yoga Teacher Training &amp; Certification in Hyderabad
                </h2>
                <p className="fy-hero-para mb-3">
                  Complete your face yoga teacher training in Hyderabad through structured theory, practical learning, and assessment.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  After successfully meeting the required attendance, assignments, practical training, and final assessment, you will receive the <strong>PranaFlow Academy Face Yoga Teacher Training Certificate</strong>.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  This certificate records completion of the academy’s programme. It is <strong>not presented as a Yoga Alliance teacher credential or a medical qualification</strong>.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1.5rem' }}>
                  Personal practice sessions and standalone workshops do not automatically fulfil the certification requirements.
                </p>

                <div className="fy-trust-badges mt-2 mb-2" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', background: 'var(--fy-cream-card)', padding: '12px 20px', borderRadius: '30px', border: '1px solid var(--fy-sage-border)', fontSize: '0.88rem', fontWeight: '600', color: 'var(--fy-sage-dark)' }}>
                  <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '1.1rem' }} aria-hidden="true"></i>
                  <span>PranaFlow Academy Verified Certificate · Practical Assessment · Professional Recognition</span>
                </div>
              </div>

              <div className="col-lg-5 text-center">
                <div style={{ background: 'var(--fy-cream-card)', border: '2px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '36px 28px', boxShadow: 'var(--fy-shadow-card)' }}>
                  <i className="bi bi-award-fill" style={{ fontSize: '3.5rem', color: 'var(--fy-sage-primary)' }} aria-hidden="true"></i>
                  <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginTop: '16px', marginBottom: '8px' }}>
                    PranaFlow Academy Face Yoga Teacher Training Certificate
                  </h3>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--fy-sage-primary)', marginBottom: '14px' }}>
                    Issued by PranaFlow Academy upon successful completion of the required training and assessment.
                  </div>
                  <p style={{ fontSize: '0.86rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                    Awarded after completing the 30-hour course, in-person training, assignments, practical demonstration, and teaching evaluation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 16 — SPECIAL ENQUIRIES (BRIDAL & WORKSHOPS)
            ===================================================================== */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <span className="fy-eyebrow">TELL US WHAT YOU ARE PLANNING</span>
              <h2 className="fy-serif-heading">Bridal Face Yoga &amp; Workshop Enquiries in Hyderabad</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-special-grid">
              {/* Special 1: Bridal */}
              <div className="fy-special-card">
                <div>
                  <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: 'var(--fy-blush-light)', color: 'var(--fy-blush-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', marginBottom: '16px' }}>
                    <i className="bi bi-gem" aria-hidden="true"></i>
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '12px' }}>
                    Bridal Face Yoga Hyderabad
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '20px' }}>
                    Preparing for your wedding and interested in a guided self-care practice? Ask about one-to-one sessions and discuss your timeline and learning goals. We do not promise a particular appearance by your wedding date.
                  </p>
                </div>
                <button
                  type="button"
                  className="fy-btn-secondary w-100"
                  onClick={() => handleSpecialEnquiryAndScroll('Interested in Bridal Face Yoga sessions: ')}
                >
                  Enquire About Bridal Sessions <i className="bi bi-arrow-right" aria-hidden="true"></i>
                </button>
              </div>

              {/* Special 2: Corporate */}
              <div className="fy-special-card">
                <div>
                  <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', marginBottom: '16px' }}>
                    <i className="bi bi-building" aria-hidden="true"></i>
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '12px' }}>
                    Corporate Face Yoga Workshop Hyderabad
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '20px' }}>
                    Planning a wellbeing activity for your workplace or IT team? Share your location, group size, preferred language, and proposed date so our team can assess whether a suitable workshop can be arranged.
                  </p>
                </div>
                <button
                  type="button"
                  className="fy-btn-secondary w-100"
                  onClick={() => handleSpecialEnquiryAndScroll('Interested in Corporate Face Yoga Workshop: ')}
                >
                  Discuss a Corporate Workshop <i className="bi bi-arrow-right" aria-hidden="true"></i>
                </button>
              </div>

              {/* Special 3: Group Workshop */}
              <div className="fy-special-card">
                <div>
                  <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: 'var(--fy-sage-light)', color: 'var(--fy-sage-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', marginBottom: '16px' }}>
                    <i className="bi bi-people" aria-hidden="true"></i>
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginBottom: '12px' }}>
                    Face Yoga Workshop Hyderabad
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '20px' }}>
                    Interested in an introductory group session? Contact us to discuss your requirements and ask about workshop availability.
                  </p>
                </div>
                <button
                  type="button"
                  className="fy-btn-secondary w-100"
                  onClick={() => handleSpecialEnquiryAndScroll('Interested in Face Yoga Group Workshop: ')}
                >
                  Ask About a Workshop <i className="bi bi-arrow-right" aria-hidden="true"></i>
                </button>
              </div>
            </div>

            <div className="small text-muted fst-italic text-center mt-4" style={{ maxWidth: '820px', margin: '24px auto 0' }}>
              Special sessions and workshops are subject to confirmation of teacher availability, format, location, and fees.
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 17 — ONLINE ALTERNATIVE
            ===================================================================== */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-plan-box" style={{ background: '#ffffff' }}>
              <div className="row gy-4 align-items-center">
                <div className="col-lg-8">
                  <span className="fy-eyebrow">PREFER LEARNING FROM HOME?</span>
                  <h2 className="fy-serif-heading mb-3">Explore Our Live Online Face Yoga Course</h2>
                  <p className="fy-hero-para">
                    You can also learn with PranaFlow Academy through our separate live online teacher training programme.
                  </p>
                  <p style={{ fontSize: '0.95rem', color: 'var(--fy-text-main)', lineHeight: 1.65, marginBottom: '16px' }}>
                    For <strong>online face yoga classes, IST timings</strong>, fees, recordings, and upcoming online batches, visit the main Face Yoga course page.
                  </p>
                  <div className="mt-3">
                    <Link
                      to="/courses/face-yoga-teacher-training-course-online"
                      className="fy-btn-primary"
                    >
                      View Online Face Yoga Course <i className="bi bi-arrow-right ms-1" aria-hidden="true"></i>
                    </Link>
                  </div>
                </div>

                <div className="col-lg-4 text-center">
                  <div style={{ background: 'var(--fy-cream-card)', border: '1px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-md)', padding: '24px' }}>
                    <i className="bi bi-laptop text-success" style={{ fontSize: '3rem' }} aria-hidden="true"></i>
                    <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--fy-sage-dark)', marginTop: '12px', marginBottom: '8px' }}>
                      Separate Live Online Cohorts
                    </h3>
                    <p style={{ fontSize: '0.88rem', color: 'var(--fy-text-muted)', lineHeight: 1.6, margin: 0 }}>
                      For classroom and one-to-one learning in Hyderabad, continue with the enquiry form on this page.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 18 — FIND THE CENTRE
            ===================================================================== */}
        <section id="location" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <span className="fy-eyebrow">VISIT OUR HYDERABAD CENTRE</span>
              <h2 className="fy-serif-heading">Looking for “Face Yoga Near Me” in Hyderabad?</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-centre-card">
              <div className="row gy-4 align-items-center">
                <div className="col-lg-12 text-center" style={{ maxWidth: '880px', margin: '0 auto' }}>
                  <p className="fy-hero-para" style={{ marginBottom: '16px' }}>
                    If you are comparing a <strong>face yoga studio in Hyderabad</strong> or looking for an <strong>instructor in Hyderabad</strong>, speak with our team about our centre location, teaching arrangements, and available sessions.
                  </p>
                  <p style={{ fontSize: '0.92rem', color: 'var(--fy-text-muted)', lineHeight: 1.65, margin: 0 }}>
                    Ask for the exact address and directions before travelling. Arrange your visit in advance so the team can confirm a suitable time.
                  </p>
                </div>
              </div>

              <div className="fy-centre-details-grid">
                <div className="fy-centre-item">
                  <div className="fy-centre-item-icon"><i className="bi bi-geo-alt-fill" aria-hidden="true"></i></div>
                  <div>
                    <div className="fy-centre-item-label">Centre</div>
                    <div className="fy-centre-item-val">PranaFlow Academy — Hyderabad</div>
                  </div>
                </div>

                <div className="fy-centre-item">
                  <div className="fy-centre-item-icon"><i className="bi bi-whatsapp" aria-hidden="true"></i></div>
                  <div>
                    <div className="fy-centre-item-label">WhatsApp</div>
                    <div className="fy-centre-item-val">
                      <a href="https://wa.me/919548354084?text=Hello%20PranaFlow%20Academy%2C%20please%20share%20the%20exact%20address%20and%20location%20details%20of%20your%20Hyderabad%20centre%20for%20the%20Face%20Yoga%20course." target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                        +91 95483 54084
                      </a>
                    </div>
                  </div>
                </div>

                <div className="fy-centre-item">
                  <div className="fy-centre-item-icon"><i className="bi bi-envelope-fill" aria-hidden="true"></i></div>
                  <div>
                    <div className="fy-centre-item-label">Email</div>
                    <div className="fy-centre-item-val">
                      <a href="mailto:pranaflowacademy@gmail.com" style={{ color: 'inherit', textDecoration: 'none' }}>
                        pranaflowacademy@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center d-flex flex-wrap gap-3 justify-content-center mt-3">
                <a
                  href="https://wa.me/919548354084?text=Hello%20PranaFlow%20Academy%2C%20please%20share%20the%20exact%20address%20and%20location%20details%20of%20your%20Hyderabad%20centre%20for%20the%20Face%20Yoga%20course."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fy-btn-primary"
                >
                  <i className="bi bi-whatsapp me-1" aria-hidden="true"></i> Get Centre Location on WhatsApp
                </a>

                <Link
                  to="/Centre/hydrabad"
                  className="fy-btn-secondary"
                >
                  Explore Our Hyderabad Centre <i className="bi bi-arrow-right ms-1" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 19 — FREQUENTLY ASKED QUESTIONS
            ===================================================================== */}
        <section id="faq" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <h2 className="fy-serif-heading">Questions About Face Yoga Classes in Hyderabad</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-faq-grid">
              {faqs.map((faq, idx) => (
                <div key={idx} className="fy-faq-item">
                  <button
                    className="fy-faq-btn"
                    aria-expanded={activeFaq === idx}
                    type="button"
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  >
                    <span>{faq.q}</span>
                    <div className="fy-faq-icon">
                      <i className={`bi ${activeFaq === idx ? 'bi-dash' : 'bi-plus'}`} aria-hidden="true"></i>
                    </div>
                  </button>
                  {activeFaq === idx && (
                    <div className="fy-faq-ans" style={{ display: 'block' }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================================
            SECTION 20 — FINAL CALL TO ACTION
            ===================================================================== */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-cta-banner">
              <div className="row gy-4 align-items-center">
                <div className="col-lg-8">
                  <h2 className="fy-cta-heading">
                    Begin Your Face Yoga Learning Journey in Hyderabad
                  </h2>
                  <p className="fy-cta-sub" style={{ marginBottom: '14px' }}>
                    Learn in person, ask questions directly, and build your understanding through guided practice. Choose classroom teacher training or discuss one-to-one sessions with our Hyderabad team.
                  </p>

                  <div className="fy-btn-group" style={{ marginBottom: '20px' }}>
                    <button
                      type="button"
                      className="fy-btn-primary fy-cta-btn-white"
                      onClick={() => scrollToSection('enquiry-section')}
                    >
                      Get Hyderabad Course Details <i className="bi bi-arrow-right-circle-fill" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      className="fy-btn-secondary fy-cta-btn-outline"
                      onClick={() => handleSelectFormatAndScroll('One-to-One Teacher Training')}
                    >
                      Ask About One-to-One Training
                    </button>
                  </div>

                  <div style={{ fontSize: '0.92rem', color: '#C3DFC8' }}>
                    Prefer online?{' '}
                    <Link
                      to="/courses/face-yoga-teacher-training-course-online"
                      style={{ color: '#ffffff', fontWeight: 700, textDecoration: 'underline' }}
                    >
                      View Our Online Face Yoga Course →
                    </Link>
                  </div>
                </div>

                <div className="col-lg-4 text-center">
                  <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--fy-radius-lg)', padding: '28px 20px' }}>
                    <div style={{ fontWeight: 800, fontSize: '1.25rem', color: '#ffffff', marginBottom: '6px' }}>
                      30 Hours / 1 Month
                    </div>
                    <div style={{ fontSize: '0.92rem', color: '#C3DFC8', marginBottom: '6px' }}>
                      In-Person Learning · Hyderabad
                    </div>
                    <div style={{ fontSize: '0.92rem', color: '#C3DFC8', marginBottom: '6px' }}>
                      One-to-One Options
                    </div>
                    <div style={{ fontSize: '0.88rem', color: '#fbbf24', fontWeight: 600 }}>
                      PranaFlow Academy Certification
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Course Notice / Disclaimer */}
        <div className="fy-container pb-5">
          <div className="fy-page-disclaimer">
            <strong>Course notice:</strong> This programme is for wellness education and teacher training. It does not diagnose, treat, or cure medical conditions or replace medical care. Cosmetic outcomes are not guaranteed. Certification is awarded only after successful completion of the programme’s requirements.
          </div>
        </div>

      </main>

      <PublicFooter />
    </div>
  );
};

export default HydrabadFaceYoga;
